import { classnames } from '@aibees/shared/utils';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { memo, useCallback, useMemo, useState } from 'react';
import { Icon } from '../icon';
import type { DateRangePickerProps as Props } from './types';
import { getDateRangePickerValues } from './utils';

export const DateRangePicker = memo(
  ({ className, value, onChangeStart, onChangeEnd }: Props) => {
    const theme = useTheme();

    const [open, setOpen] = useState<number | boolean>(false);

    const valueDayjs = useMemo(() => {
      return getDateRangePickerValues(value);
    }, [value]);

    const handleChangeStart = useCallback(
      (v: Dayjs | undefined) => {
        onChangeStart?.(v);
      },
      [onChangeStart]
    );

    const handleChangeEnd = useCallback(
      (v: Dayjs | undefined) => {
        onChangeEnd?.(v);
      },
      [onChangeEnd]
    );

    const endAdornment = useCallback(
      (start: boolean) => {
        return (
          <InputAdornment position="end">
            <IconButton
              size="xsmall"
              onClick={() =>
                valueDayjs[start ? 0 : 1]
                  ? start
                    ? handleChangeStart(undefined)
                    : handleChangeEnd(undefined)
                  : setOpen(start ? 1 : 2)
              }
            >
              <Icon
                name={
                  valueDayjs[start ? 0 : 1] ? 'close' : 'calendar-today-outline'
                }
                size={16}
                color={'grey.400'}
              />
            </IconButton>
          </InputAdornment>
        );
      },
      [handleChangeEnd, handleChangeStart, valueDayjs, setOpen]
    );

    const slotPropsFrom = useMemo(
      () => ({
        textField: {
          size: 'small' as const,
          inputProps: {
            onClick: () => setOpen(1),
          },
          InputProps: {
            sx: { paddingRight: theme.spacing(0.5) },
            endAdornment: endAdornment(true),
          },
          placeholder: 'From',
        },
      }),
      [theme, endAdornment]
    );

    const slotPropsTo = useMemo(
      () => ({
        textField: {
          size: 'small' as const,
          inputProps: {
            onClick: () => setOpen(2),
          },
          InputProps: {
            sx: { paddingRight: theme.spacing(0.5) },
            endAdornment: endAdornment(false),
          },
          placeholder: 'To',
        },
      }),
      [theme, endAdornment]
    );

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          className={classnames('DateRangePicker', className)}
        >
          <DatePicker
            open={open === 1}
            onClose={() => setOpen(false)}
            slotProps={slotPropsFrom}
            value={valueDayjs[0]}
            onAccept={(v: unknown) => handleChangeStart(v as Dayjs)}
          />
          -
          <DatePicker
            open={open === 2}
            onClose={() => setOpen(false)}
            slotProps={slotPropsTo}
            value={valueDayjs[1]}
            onAccept={(v: unknown) => handleChangeEnd(v as Dayjs)}
          />
        </Box>
      </LocalizationProvider>
    );
  }
);
